/**
 * Annehmen und Ablehnen eines Änderungsvorschlags (nur Team).
 *
 * Annehmen: neues HTML mit aenderungenAnwenden (außerhalb der Stellen
 * Zeichen für Zeichen gleich), speichern als Version „Vorschlag übernommen",
 * dann je Stelle mail_feedback_entscheiden. Stellen, die sich inzwischen
 * geändert haben, bleiben offen — die arbeitet man von Hand ein.
 */
import { useMemo, useState } from 'react';
import { useToast } from '../Toast';
import { aenderungenAnwenden } from '../../lib/zusammenarbeit';
import { istKonflikt, type FeedbackEintrag, type PlanungsDetail, type WerkstattAdapter } from '../../lib/planung';
import {
  KONFLIKT_TEXT, aenderungOffen, betreffOffen, fehlerText, gespeicherteStelle, istVersendet, stellenText,
} from './werkstattHilfen';

/** Warum hier nicht übernommen werden kann (null = es geht). 'rechte' mit darfEntscheiden: nur ablehnen/erledigen. */
export type UebernahmeSperre = 'rechte' | 'assistent' | 'versendet' | null;

export interface Uebernahme {
  darfEntscheiden: boolean;
  sperre: UebernahmeSperre;
  /** Was gerade läuft: 'alle', 'a<i>' (annehmen), 'r<i>' (ablehnen), 'erledigt' */
  laeuft: string | null;
  /** Stellen, die sich laut Probe oder Versuch nicht mehr übernehmen lassen */
  passtNicht: Set<number>;
  /** Stellen, die schon so in der Mail stehen */
  schonDrin: Set<number>;
  offeneIndizes: number[];
  annehmen: (indizes: number[] | 'alle') => Promise<void>;
  ablehnen: (index: number) => Promise<void>;
  erledigen: () => Promise<void>;
}

/**
 * `erstFragen`: vor allem, wonach die Mail neu lädt — bei ungespeicherten
 * Änderungen fragt die Werkstatt erst nach.
 */
export function useVorschlagUebernehmen(
  eintrag: FeedbackEintrag, mail: PlanungsDetail, adapter: WerkstattAdapter, erstFragen?: (weiter: () => void) => void,
): Uebernahme {
  const toast = useToast();
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [versuchtNicht, setVersuchtNicht] = useState<{ md5: string; indizes: number[] } | null>(null);

  const darfEntscheiden = adapter.darfEntscheiden && !!adapter.entscheiden;
  // Übernehmen nur mit inhaltSpeichern (die Seite gibt es nur, wenn die Mail es erlaubt)
  const sperre: UebernahmeSperre = !darfEntscheiden ? 'rechte'
    : mail.aus_assistent ? 'assistent'
      : istVersendet(mail) ? 'versendet'
        : !adapter.inhaltSpeichern ? 'rechte' : null;
  const fragen = (weiter: () => void) => (erstFragen ? erstFragen(weiter) : weiter());

  const offeneIndizes = useMemo(() => {
    if (eintrag.art !== 'vorschlag' || eintrag.ergebnis) return [];
    const out: number[] = [];
    if (betreffOffen(eintrag)) out.push(-1);
    (eintrag.aenderungen ?? []).forEach((a, i) => { if (aenderungOffen(a)) out.push(i); });
    return out;
  }, [eintrag]);

  // Probe: lässt sich jede offene Stelle noch sauber übernehmen?
  const probe = useMemo(() => {
    const passtNicht = new Set<number>();
    const schonDrin = new Set<number>();
    const text = offeneIndizes.filter((i) => i >= 0);
    if (sperre || !text.length) return { passtNicht, schonDrin };
    try {
      const r = aenderungenAnwenden(mail.html, eintrag.aenderungen ?? [], text);
      for (const e of r.ergebnisse) {
        if (e.ok) continue;
        if (e.grund === 'schon_angewendet') schonDrin.add(e.index);
        else passtNicht.add(e.index);
      }
    } catch {
      text.forEach((i) => passtNicht.add(i));
    }
    return { passtNicht, schonDrin };
  }, [sperre, offeneIndizes, mail.html, eintrag.aenderungen]);

  const passtNicht = useMemo(() => {
    if (!versuchtNicht || versuchtNicht.md5 !== mail.md5) return probe.passtNicht;
    return new Set([...probe.passtNicht, ...versuchtNicht.indizes]);
  }, [probe.passtNicht, versuchtNicht, mail.md5]);

  async function annehmenJetzt(wahl: number[] | 'alle') {
    if (sperre || !adapter.inhaltSpeichern || !adapter.entscheiden) return;
    const gewuenscht = wahl === 'alle' ? offeneIndizes : wahl.filter((i) => offeneIndizes.includes(i));
    const textIdx = gewuenscht.filter((i) => i >= 0 && !passtNicht.has(i));
    const mitBetreff = gewuenscht.includes(-1) && !!eintrag.betreff_vorschlag;
    if (!textIdx.length && !mitBetreff) {
      toast.warning('Diese Stelle hat sich inzwischen geändert — bitte von Hand einarbeiten.');
      return;
    }
    const aenderungen = eintrag.aenderungen ?? [];
    let html = mail.html;
    const uebernommen: number[] = [];
    const nicht: number[] = [];
    if (textIdx.length) {
      const r = aenderungenAnwenden(mail.html, aenderungen, textIdx);
      html = r.html;
      for (const e of r.ergebnisse) {
        if (e.ok || e.grund === 'schon_angewendet') uebernommen.push(e.index);
        else nicht.push(e.index);
      }
    }
    if (nicht.length) setVersuchtNicht({ md5: mail.md5, indizes: nicht });
    if (!uebernommen.length && !mitBetreff) {
      toast.warning('Diese Stelle hat sich inzwischen geändert — bitte von Hand einarbeiten.');
      return;
    }
    setLaeuft(wahl === 'alle' ? 'alle' : `a${gewuenscht[0]}`);
    try {
      if (html !== mail.html || mitBetreff) {
        await adapter.inhaltSpeichern({
          html,
          basisMd5: mail.md5,
          quelle: 'vorschlag',
          betreff: mitBetreff ? eintrag.betreff_vorschlag : undefined,
          notiz: `Vorschlag von ${eintrag.autor_name} übernommen`,
        });
      }
      // nacheinander — die Datenbank setzt den Eintrag, sobald alles entschieden ist
      for (const i of [...(mitBetreff ? [-1] : []), ...uebernommen]) await adapter.entscheiden(eintrag.id, 'uebernommen', gespeicherteStelle(eintrag, i));
      const n = uebernommen.length + (mitBetreff ? 1 : 0);
      toast.success(nicht.length
        ? `${stellenText(n)} übernommen. ${stellenText(nicht.length)} bitte von Hand einarbeiten.`
        : n === 1 ? 'Übernommen — die Mail ist aktualisiert.' : `${stellenText(n)} übernommen — die Mail ist aktualisiert.`);
      await adapter.neuLaden();
    } catch (e) {
      if (istKonflikt(e)) {
        toast.warning(KONFLIKT_TEXT);
        await adapter.neuLaden();
      } else toast.error(fehlerText(e));
    } finally {
      setLaeuft(null);
    }
  }

  async function ablehnenJetzt(index: number) {
    if (!darfEntscheiden || !adapter.entscheiden) return;
    setLaeuft(`r${index}`);
    try {
      await adapter.entscheiden(eintrag.id, 'abgelehnt', gespeicherteStelle(eintrag, index));
      toast.success(index === -1 ? 'Betreff-Vorschlag abgelehnt.' : 'Abgelehnt.');
      await adapter.neuLaden();
    } catch (e) {
      toast.error(fehlerText(e));
    } finally {
      setLaeuft(null);
    }
  }

  async function erledigenJetzt() {
    if (!darfEntscheiden || !adapter.entscheiden) return;
    setLaeuft('erledigt');
    try {
      await adapter.entscheiden(eintrag.id, 'erledigt');
      toast.success('Als erledigt markiert.');
      await adapter.neuLaden();
    } catch (e) {
      toast.error(fehlerText(e));
    } finally {
      setLaeuft(null);
    }
  }

  return {
    darfEntscheiden, sperre, laeuft, passtNicht, schonDrin: probe.schonDrin, offeneIndizes,
    annehmen: async (wahl) => fragen(() => { void annehmenJetzt(wahl); }),
    ablehnen: async (index) => fragen(() => { void ablehnenJetzt(index); }),
    erledigen: async () => fragen(() => { void erledigenJetzt(); }),
  };
}
